import type {
  CreditBalanceResponse,
  FamilyProfileRecord,
  FamilyProfileSyncResponse,
  HistoricalConversationDetailResponse,
  HistoricalConversationMessage,
  HistoricalConversationTurnResponse,
  HistoricalFigureCatalogueResponse,
  HistoricalFigureProfile,
  HistoricalFigureSummary,
  HistoricalVoiceSession,
  ParentAccountStatusResponse,
  PublicCuratorCatalogueResponse,
  PublicCuratorStoryDetailResponse,
  ReadingJourneyCatalogueResponse,
  StoryAssetsResponse,
  StoryDraft,
  StoryLevel,
  StoryRequest,
  StoryVersion,
  WebParentSessionResponse
} from "@lts/shared";
import { api, ApiError, apiAssetUrl } from "./api";

type ViewName = "home" | "library" | "create" | "search" | "figures" | "family" | "draft" | "reader" | "figure" | "conversation";

interface AppConfig {
  slogan: string;
  levels: Record<StoryLevel, { label: string; description: string; targetWords: [number, number] }>;
  webAccount: {
    enabled: boolean;
    clientId?: string;
    redirectUri?: string;
  };
  webVoice?: {
    enabled: boolean;
  };
}

interface AppState {
  loading: boolean;
  busy: boolean;
  view: ViewName;
  config?: AppConfig;
  session: WebParentSessionResponse;
  selectedProfileId?: string;
  account?: ParentAccountStatusResponse;
  library: StoryDraft[];
  credits?: CreditBalanceResponse;
  journeys?: ReadingJourneyCatalogueResponse;
  publicCatalogue: PublicCuratorCatalogueResponse["stories"];
  figures?: HistoricalFigureCatalogueResponse;
  activeDraft?: StoryDraft;
  activeAssets?: StoryAssetsResponse;
  activePublicStory?: PublicCuratorStoryDetailResponse;
  activeFigure?: HistoricalFigureProfile;
  conversation?: HistoricalConversationDetailResponse;
  voiceSession?: HistoricalVoiceSession;
  searchQuery: string;
  searchLevel: StoryLevel | "all";
  createFormat: "story" | "across_time";
  previousView: ViewName;
}

interface AppleAuthorizationDetail {
  authorization?: {
    code?: string;
    id_token?: string;
    state?: string;
  };
  user?: {
    email?: string;
    name?: { firstName?: string; lastName?: string };
  };
}

interface AppleIdClient {
  auth: {
    init(options: {
      clientId: string;
      scope: string;
      redirectURI: string;
      state: string;
      nonce: string;
      usePopup: boolean;
    }): void;
  };
}

interface SpeechRecognitionResultEventLike extends Event {
  results: ArrayLike<{ 0: { transcript: string } }>;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionConstructor {
  new(): SpeechRecognitionLike;
}

declare global {
  interface Window {
    AppleID?: AppleIdClient;
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

const app = requiredElement<HTMLDivElement>("app");
const toast = requiredElement<HTMLDivElement>("toast");
let toastTimer: number | undefined;
let appleScriptPromise: Promise<void> | undefined;
let appleConfiguredKey: string | undefined;
let appleExpectedState: string | undefined;
let appleExpectedNonce: string | undefined;

const state: AppState = {
  loading: true,
  busy: false,
  view: "home",
  session: { authenticated: false, profiles: [] },
  library: [],
  publicCatalogue: [],
  searchQuery: "",
  searchLevel: "all",
  createFormat: "story",
  previousView: "home"
};

const levelLabels: Record<StoryLevel, string> = {
  "6-8": "Ages 6-8",
  "9-12": "Ages 9-12",
  "13-17": "Ages 13-17",
  "18+": "Adults 18+"
};

const confidenceLabels: Record<string, string> = {
  documented_history: "Documented History",
  history_plus_tradition: "History + Tradition",
  legend_retold: "Legend Retold",
  inspired_by_historical_world: "Inspired by a Historical World",
  mystery_or_disputed_claim: "Mystery / Disputed Claim"
};

const periods = [
  { title: "Ancient World", dates: "Before 500", image: "/images/period-ancient.webp", query: "Ancient" },
  { title: "Medieval World", dates: "500-1500", image: "/images/period-medieval.webp", query: "Medieval" },
  { title: "Early Modern", dates: "1500-1775", image: "/images/period-colonial.webp", query: "Early Modern" },
  { title: "Modern Era", dates: "1775-Present", image: "/images/period-modern.webp", query: "Modern" }
];

const collections = [
  { title: "Hidden Heroes", description: "People history nearly forgot", image: "/images/collection-hidden-heroes.webp" },
  { title: "Turning Points", description: "Choices that changed what came next", image: "/images/collection-turning-points.webp" },
  { title: "Faith Under Fire", description: "Belief, courage, conflict, and consequence", image: "/images/collection-faith.webp" }
];

function requiredElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element as T;
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function safeExternalUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

function selectedProfile(): FamilyProfileRecord | undefined {
  return state.session.profiles.find((profile) => profile.id === state.selectedProfileId)
    ?? state.session.profiles[0];
}

function contentSettings(profile: FamilyProfileRecord) {
  return {
    allowsBattleStories: profile.allowsBattleStories,
    allowsTragicEndings: profile.allowsTragicEndings,
    allowsInfamousFigures: profile.allowsInfamousFigures,
    allowsSacredHistory: profile.allowsSacredHistory,
    allowsHistoricalFigureTyping: profile.allowsHistoricalFigureTyping,
    allowsReligiousConflict: profile.allowsReligiousConflict,
    allowsMysteriesAndTheories: profile.allowsMysteriesAndTheories
  };
}

function setBusy(value: boolean): void {
  state.busy = value;
  render();
}

function showToast(message: string, kind: "normal" | "error" = "normal"): void {
  window.clearTimeout(toastTimer);
  toast.textContent = message;
  toast.className = `toast visible${kind === "error" ? " error" : ""}`;
  toastTimer = window.setTimeout(() => {
    toast.className = "toast";
  }, 4800);
}

function handleError(error: unknown): void {
  if (error instanceof ApiError) {
    showToast(error.redirectMessage || error.message, "error");
    if (error.code === "account_paused") {
      state.view = "family";
      void loadProfileData();
    }
    return;
  }
  console.error(error);
  showToast("Something did not finish correctly. Please try again.", "error");
}

function navigate(view: ViewName): void {
  if (state.account?.paused && view !== "family") {
    state.view = "family";
    showToast("This account is paused. Only Family settings are available.", "error");
  } else {
    state.previousView = state.view;
    state.view = view;
  }
  window.scrollTo({ top: 0, behavior: "smooth" });
  render();
}

async function boot(): Promise<void> {
  render();
  const [configResult, sessionResult, catalogueResult] = await Promise.allSettled([
    api<AppConfig>("/config"),
    api<WebParentSessionResponse>("/web/session"),
    api<PublicCuratorCatalogueResponse>("/catalog/curators-pick?limit=24")
  ]);
  if (configResult.status === "fulfilled") state.config = configResult.value;
  if (sessionResult.status === "fulfilled") state.session = sessionResult.value;
  if (catalogueResult.status === "fulfilled") state.publicCatalogue = catalogueResult.value.stories;
  state.selectedProfileId = state.session.profiles[0]?.id;
  state.loading = false;
  render();
  if (selectedProfile()) await loadProfileData();
}

async function loadProfileData(): Promise<void> {
  const profile = selectedProfile();
  if (!profile) {
    state.account = undefined;
    state.library = [];
    state.credits = undefined;
    state.journeys = undefined;
    state.figures = undefined;
    render();
    return;
  }
  state.selectedProfileId = profile.id;
  try {
    const [account, credits] = await Promise.all([
      api<ParentAccountStatusResponse>(`/profiles/${encodeURIComponent(profile.id)}/parent-account`),
      api<CreditBalanceResponse>(`/profiles/${encodeURIComponent(profile.id)}/credits`)
    ]);
    state.account = account;
    state.credits = credits;
    if (account.paused) {
      state.library = [];
      state.journeys = undefined;
      state.figures = undefined;
      state.view = "family";
      render();
      return;
    }
    const [library, journeys, figures, catalogue] = await Promise.all([
      api<{ stories: StoryDraft[] }>(`/profiles/${encodeURIComponent(profile.id)}/library`),
      api<ReadingJourneyCatalogueResponse>(`/profiles/${encodeURIComponent(profile.id)}/reading-journeys?level=${encodeURIComponent(profile.storyLevel)}`),
      api<HistoricalFigureCatalogueResponse>(`/figures?profileId=${encodeURIComponent(profile.id)}&level=${encodeURIComponent(profile.storyLevel)}`),
      api<PublicCuratorCatalogueResponse>(`/catalog/curators-pick?limit=24&level=${encodeURIComponent(profile.storyLevel)}`)
    ]);
    state.library = library.stories;
    state.journeys = journeys;
    state.figures = figures;
    state.publicCatalogue = catalogue.stories;
  } catch (error) {
    handleError(error);
  } finally {
    render();
  }
}

function render(): void {
  if (state.loading) {
    app.innerHTML = `<main class="loading-screen"><div><div class="loading-mark"></div><p>Opening the library...</p></div></main>`;
    return;
  }

  app.innerHTML = `${renderOnboarding()}${renderShell()}`;
  if (state.view === "family" && !state.session.authenticated) {
    void configureAppleSignIn();
  }
}

function renderOnboarding(): string {
  if (window.localStorage.getItem("lts_web_onboarded") === "true") return "";
  return `
    <section class="onboarding" aria-labelledby="welcome-title">
      <img class="onboarding-background" src="/images/journey-welcome.webp" alt="">
      <div class="onboarding-content">
        <img class="onboarding-logo" src="/images/app-icon.webp" alt="Learning Through Stories emblem">
        <h1 id="welcome-title" class="display-title">Learning<br>Through Stories</h1>
        <p class="onboarding-slogan">History worth remembering.<br>Stories worth becoming.</p>
        <button class="primary-button" data-action="finish-onboarding">Begin the Journey</button>
      </div>
    </section>`;
}

function renderShell(): string {
  const activeNav = ["draft", "reader"].includes(state.view) ? "library"
    : ["figure", "conversation"].includes(state.view) ? "figures"
    : state.view;
  const nav = [
    ["home", "⌂", "Home"],
    ["library", "▤", "Library"],
    ["create", "+", "Create"],
    ["figures", "◉", "Figures"],
    ["family", "◎", "Family"]
  ];
  const navButtons = nav.map(([view, glyph, label]) => `
    <button class="nav-button${activeNav === view ? " active" : ""}" data-view="${view}" ${state.account?.paused && view !== "family" ? "disabled" : ""}>
      <span class="nav-glyph" aria-hidden="true">${glyph}</span><span>${label}</span>
    </button>`).join("");

  return `
    <div class="app-shell">
      <aside class="side-nav" aria-label="Main navigation">
        <div class="brand-lockup">
          <img src="/images/app-icon.webp" alt="">
          <span class="brand-name">Learning Through Stories</span>
        </div>
        <nav class="nav-list">${navButtons}</nav>
        <p class="side-footer">History worth remembering.<br>Stories worth becoming.</p>
      </aside>
      <div class="app-column">
        ${renderTopbar()}
        <main id="main-content" class="main-content">${renderView()}</main>
      </div>
      <nav class="mobile-nav" aria-label="Main navigation">${navButtons}</nav>
    </div>`;
}

function renderTopbar(): string {
  const profile = selectedProfile();
  const profiles = state.session.profiles.map((item) => `
    <option value="${escapeHtml(item.id)}" ${item.id === profile?.id ? "selected" : ""}>${escapeHtml(item.displayName)} · ${escapeHtml(item.storyLevel)}</option>`).join("");
  return `
    <header class="topbar">
      <h2 class="topbar-title">${escapeHtml(viewTitle())}</h2>
      <div class="topbar-actions">
        ${profile ? `<label class="hidden" for="profile-select">Family profile</label><select id="profile-select" class="profile-select">${profiles}</select>` : ""}
        <button class="icon-button" data-view="search" aria-label="Search stories and figures" title="Search">⌕</button>
      </div>
    </header>`;
}

function viewTitle(): string {
  const titles: Record<ViewName, string> = {
    home: "Learning Through Stories",
    library: "Your Library",
    create: "Create a Story",
    search: "Search History",
    figures: "Historical Figures",
    family: "Family",
    draft: "Draft Review",
    reader: "Story Reader",
    figure: state.activeFigure?.name ?? "Historical Figure",
    conversation: state.conversation?.conversation.figure.name ?? "Conversation"
  };
  return titles[state.view];
}

function renderView(): string {
  if (state.account?.paused && state.view !== "family") return renderPausedAccount();
  switch (state.view) {
    case "home": return renderHome();
    case "library": return renderLibrary();
    case "create": return renderCreate();
    case "search": return renderSearch();
    case "figures": return renderFigures();
    case "family": return renderFamily();
    case "draft": return renderDraftReview();
    case "reader": return renderReader();
    case "figure": return renderFigureDetail();
    case "conversation": return renderConversation();
  }
}

function renderHome(): string {
  const profile = selectedProfile();
  const featuredFigure = state.figures?.featured;
  const publicFeature = state.publicCatalogue[0];
  const featureImage = apiAssetUrl(featuredFigure?.figure.portrait?.url) ?? "/images/journey-welcome.webp";
  const title = featuredFigure?.figure.name ?? publicFeature?.story.title ?? "A Library of Living History";
  const subtitle = featuredFigure?.figure.pageSubtitle ?? "Source-grounded stories for curious families";
  const description = featuredFigure?.figure.pageTitle
    ? `${featuredFigure.figure.pageTitle}. Explore a carefully bounded conversation based on approved historical sources.`
    : publicFeature?.story.summary ?? "Read reviewed stories, follow chapter journeys, and meet people from the past with clear sources and age-appropriate guardrails.";
  const focuses = featuredFigure?.focuses.slice(0, 4) ?? [];
  const recent = state.library.slice(0, 3);

  return `
    <section class="feature-band" aria-labelledby="daily-feature-title">
      <img class="feature-image" src="${escapeHtml(featureImage)}" alt="${escapeHtml(featuredFigure?.figure.portrait?.altText ?? "Historic library at night")}">
      <div class="feature-copy">
        <p class="eyebrow">${featuredFigure ? "Figure of the Day" : "Welcome to the Library"}</p>
        <h1 id="daily-feature-title">${escapeHtml(title)}</h1>
        <p class="feature-subtitle">${escapeHtml(subtitle)}</p>
        <p class="feature-description">${escapeHtml(description)}</p>
        <div class="feature-actions">
          ${featuredFigure ? `<button class="primary-button" data-open-figure="${escapeHtml(featuredFigure.figure.id)}">Explore ${escapeHtml(featuredFigure.figure.name)}</button>` : `<button class="primary-button" data-view="library">Explore Stories</button>`}
          ${profile ? `<button class="secondary-button" data-view="create">Create Story</button>` : `<button class="secondary-button" data-view="family">Parent Sign In</button>`}
        </div>
        ${focuses.length ? `<div class="focus-grid">${focuses.map((focus) => `<button class="focus-button" data-open-figure="${escapeHtml(featuredFigure!.figure.id)}" title="${escapeHtml(focus.description)}"><strong>${escapeHtml(focus.title)}</strong><br><span class="muted">${escapeHtml(focus.description)}</span></button>`).join("")}</div>` : ""}
      </div>
    </section>
    ${recent.length ? renderStorySection("Continue Your Journey", "Private family stories", recent, true) : ""}
    <section class="section-band" aria-labelledby="period-heading">
      <div class="section-header"><h2 id="period-heading">Explore Time Periods</h2><button class="text-action" data-view="search">Browse all history</button></div>
      <div class="horizontal-grid">${periods.map(renderPeriodCard).join("")}</div>
    </section>
    <section class="section-band" aria-labelledby="collection-heading">
      <div class="section-header"><h2 id="collection-heading">Story Collections</h2><button class="text-action" data-view="search">Browse all collections</button></div>
      <div class="collection-grid">${collections.map(renderCollectionCard).join("")}</div>
    </section>
    ${state.publicCatalogue.length ? renderPublicSection("Curator's Pick", "Help build a library of truth.", state.publicCatalogue.slice(0, 6)) : ""}
    ${state.journeys?.journeys.length ? renderJourneySection(state.journeys.journeys.slice(0, 4)) : ""}
    <section class="section-band chat-promo">
      <div><p class="eyebrow">Historical Figures</p><h2>Ask questions. Hear their stories.</h2><p>Each conversation stays within the figure's approved life, people, places, writings, and events.</p></div>
      <button class="primary-button" data-view="figures">Meet the Figures</button>
    </section>
    <section class="trust-band"><p class="eyebrow">Sources Matter</p><h2>Truth and imagination stay clearly marked.</h2><p class="muted">Stories distinguish documented evidence, tradition, uncertainty, and imagined connective detail. Sources appear after the story and are never read aloud.</p></section>`;
}

function renderPeriodCard(period: typeof periods[number]): string {
  return `<button class="period-card" data-browse-query="${escapeHtml(period.query)}"><img src="${period.image}" alt=""><span class="period-card-copy"><strong>${escapeHtml(period.title)}</strong><span>${escapeHtml(period.dates)}</span></span></button>`;
}

function renderCollectionCard(collection: typeof collections[number]): string {
  return `<button class="collection-card" data-browse-query="${escapeHtml(collection.title)}"><img src="${collection.image}" alt=""><span class="collection-card-copy"><strong>${escapeHtml(collection.title)}</strong><span>${escapeHtml(collection.description)}</span></span></button>`;
}

function renderStorySection(title: string, subtitle: string, stories: StoryDraft[], privateStories: boolean): string {
  return `<section class="section-band"><div class="section-header"><div><h2>${escapeHtml(title)}</h2><span class="muted">${escapeHtml(subtitle)}</span></div>${privateStories ? `<button class="text-action" data-view="library">View all</button>` : ""}</div><div class="story-grid">${stories.map(renderPrivateStoryCard).join("")}</div></section>`;
}

function renderPrivateStoryCard(draft: StoryDraft): string {
  const version = approvedVersion(draft) ?? draft.versionA;
  return `<button class="story-card" data-open-draft="${escapeHtml(draft.id)}"><div class="card-copy"><p class="eyebrow">${escapeHtml(statusLabel(draft.status))}</p><h3>${escapeHtml(version.title)}</h3><p>${escapeHtml(version.summary)}</p><div class="metadata-row"><span class="badge">${escapeHtml(levelLabels[draft.request.level])}</span><span class="badge trust">${escapeHtml(confidenceLabels[version.confidence] ?? version.confidence)}</span>${draft.request.package !== "standard" ? `<span class="badge">Illustrated</span>` : `<span class="badge">Audio</span>`}</div></div></button>`;
}

function renderPublicSection(title: string, subtitle: string, items: PublicCuratorCatalogueResponse["stories"]): string {
  return `<section class="section-band"><div class="section-header"><div><h2>${escapeHtml(title)}</h2><span class="muted">${escapeHtml(subtitle)}</span></div><button class="text-action" data-view="library">Open catalogue</button></div><div class="story-grid">${items.map(renderPublicStoryCard).join("")}</div></section>`;
}

function renderPublicStoryCard(item: PublicCuratorCatalogueResponse["stories"][number]): string {
  const story = item.story;
  return `<button class="story-card" data-open-public="${escapeHtml(item.submission.id)}"><div class="card-copy"><p class="eyebrow">Curator's Pick</p><h3>${escapeHtml(story.title)}</h3><p>${escapeHtml(story.summary)}</p><div class="metadata-row"><span class="badge">${escapeHtml(levelLabels[story.level])}</span><span class="badge trust">${escapeHtml(confidenceLabels[story.confidence] ?? story.confidence)}</span>${story.collection ? `<span class="badge">${escapeHtml(story.collection)}</span>` : ""}</div></div></button>`;
}

function renderJourneySection(journeys: ReadingJourneyCatalogueResponse["journeys"]): string {
  return `<section class="section-band"><div class="section-header"><div><h2>Story Journeys</h2><span class="muted">Chapters unlock as the journey continues.</span></div><button class="text-action" data-view="library">View journeys</button></div><div class="journey-list">${journeys.map((journey) => `<button class="journey-card" data-open-journey="${escapeHtml(journey.id)}"><span class="journey-progress">${journey.completedChapterCount}/${journey.chapterCount}</span><span><strong>${escapeHtml(journey.title)}</strong><br><span class="muted">${escapeHtml(journey.subtitle)}</span></span><span>${journey.isComplete ? "Complete" : "Continue"}</span></button>`).join("")}</div></section>`;
}

function renderLibrary(): string {
  return `
    <div class="view-heading"><div><p class="eyebrow">Stories</p><h1>Your Library</h1><p class="muted">Private family stories first, followed by reviewed public history.</p></div><div class="button-row"><button class="primary-button" data-view="create" ${selectedProfile() ? "" : "disabled"}>Create Story</button></div></div>
    ${selectedProfile() ? (state.library.length ? renderStorySection("Private Family Library", `${state.library.length} saved ${state.library.length === 1 ? "story" : "stories"}`, state.library, false) : `<section class="empty-state"><h2>Your private shelf is ready.</h2><p class="muted">Approved stories will appear here. Text is reviewed before audio or images are generated.</p><button class="primary-button" data-view="create">Create the First Story</button></section>`) : renderSignInNotice()}
    ${state.journeys?.journeys.length ? renderJourneySection(state.journeys.journeys) : ""}
    ${state.publicCatalogue.length ? renderPublicSection("Curator's Pick Catalogue", "Reviewed by the Learning Through Stories curator.", state.publicCatalogue) : `<section class="empty-state"><h2>The public catalogue is being prepared.</h2><p class="muted">Only reviewed and parent-approved stories will appear here.</p></section>`}`;
}

function renderCreate(): string {
  const profile = selectedProfile();
  if (!profile) return renderSignInNotice("A parent account and family profile are needed before creating a private story.");
  return `
    <div class="view-heading"><div><p class="eyebrow">Guided Creation</p><h1>Create a Story</h1><p class="muted">Text comes first. Audio and images are made only after you approve a version.</p></div></div>
    <form id="create-story-form" class="form-surface guided-form">
      <div class="field"><label for="story-format">Format</label><select id="story-format" name="format"><option value="story" ${state.createFormat === "story" ? "selected" : ""}>Historical Story</option><option value="across_time" ${state.createFormat === "across_time" ? "selected" : ""}>Across Time Dialogue</option></select><small>Across Time is a guided imagined meeting, never a claim that the conversation happened.</small></div>
      <div class="field"><label for="story-level">Story level</label><select id="story-level" name="level" disabled><option>${escapeHtml(levelLabels[profile.storyLevel])}</option></select><small>Change the family story level in Family settings.</small></div>
      <div class="field full"><label for="story-topic">Person, event, place, or question</label><input id="story-topic" name="topic" maxlength="180" required placeholder="Example: Princess Gwenllian defending Wales"></div>
      <div class="field across-time-field ${state.createFormat === "across_time" ? "" : "hidden"}"><label for="participant-one">First participant</label><input id="participant-one" name="participantOne" maxlength="100" placeholder="George Washington"></div>
      <div class="field across-time-field ${state.createFormat === "across_time" ? "" : "hidden"}"><label for="participant-two">Second participant</label><input id="participant-two" name="participantTwo" maxlength="100" placeholder="Joan of Arc"></div>
      <div class="field full across-time-field ${state.createFormat === "across_time" ? "" : "hidden"}"><label for="dialogue-question">Question they should explore</label><input id="dialogue-question" name="question" maxlength="240" placeholder="How did duty shape the hardest choice each of you made?"></div>
      <div class="field"><label for="continent">Continent</label><select id="continent" name="continent"><option value="">Choose if known</option>${["Africa", "Asia", "Europe", "North America", "South America", "Australia/Oceania", "Antarctica"].map((value) => `<option>${value}</option>`).join("")}</select></div>
      <div class="field"><label for="country">Country or people</label><input id="country" name="countryOrPeople" maxlength="100" placeholder="Wales"></div>
      <div class="field"><label for="time-period">Time period</label><input id="time-period" name="timePeriod" maxlength="100" placeholder="Medieval"></div>
      <div class="field"><label for="viewpoint">Viewpoint</label><select id="viewpoint" name="viewpoint"><option value="historian">Historian guide</option><option value="witness">Witness</option><option value="defender">Defender</option><option value="civilian_family">Civilian family</option><option value="survivor">Survivor</option><option value="religious_community">Religious community</option><option value="reformer">Reformer</option><option value="explorer">Explorer</option><option value="many_sides">Many sides</option></select></div>
      <div class="field"><label for="tone">Tone</label><select id="tone" name="tone"><option value="inspiring">Inspiring</option><option value="adventurous">Adventurous</option><option value="solemn">Solemn</option><option value="mysterious">Mysterious</option><option value="funny_but_true">Funny but true</option></select></div>
      <div class="field"><label for="virtue">Virtue</label><select id="virtue" name="virtue">${["courage", "duty", "faith", "wisdom", "loyalty", "humility", "perseverance", "mercy", "self_government", "sacrifice"].map((value) => `<option value="${value}">${escapeHtml(titleCase(value))}</option>`).join("")}</select></div>
      <div class="field"><label for="package">Package</label><select id="package" name="package"><option value="standard">Standard · text + audio after approval</option><option value="illustrated">Illustrated · text + audio + images</option><option value="advanced_long">Advanced Long · longer sourced edition</option></select></div>
      <div class="form-actions"><div class="notice"><strong>One free text retry</strong><br><span class="muted">Your retry changes text only. After comparing A and B, approve one or spend one Story Credit to keep both.</span></div><div class="button-row"><button class="primary-button" type="submit" ${state.busy ? "disabled" : ""}>${state.busy ? "Creating text..." : "Create Text Draft"}</button></div></div>
    </form>`;
}

function renderDraftReview(): string {
  const draft = state.activeDraft;
  if (!draft) return `<section class="empty-state"><h1>Draft not found</h1><button class="secondary-button" data-view="library">Return to Library</button></section>`;
  if (draft.approvedVersionId) return renderApprovedDraft(draft);
  return `
    <div class="view-heading"><div><p class="eyebrow">Text Review</p><h1>Choose the Story You Love</h1><p class="muted">Audio and images remain locked until approval.</p></div><button class="quiet-button" data-view="library">Back to Library</button></div>
    <div class="draft-comparison ${draft.versionB ? "" : "single-version"}">${renderVersionSheet(draft, draft.versionA, "Version A")}${draft.versionB ? renderVersionSheet(draft, draft.versionB, "Version B") : ""}</div>
    <section class="notice" style="margin-top:16px"><strong>Your free retry only changes text.</strong> Audio and images are created after approval.</section>
    <div class="button-row" style="margin-top:16px">
      ${!draft.retryUsed ? `<button class="secondary-button" data-retry-draft="${escapeHtml(draft.id)}" ${state.busy ? "disabled" : ""}>Use Free Text Retry</button>` : ""}
      ${draft.versionB ? `<button class="secondary-button" data-keep-both="${escapeHtml(draft.id)}" ${state.busy ? "disabled" : ""}>Keep Both with 1 Story Credit</button>` : ""}
    </div>`;
}

function renderVersionSheet(draft: StoryDraft, version: StoryVersion, label: string): string {
  return `<article class="version-sheet"><p class="eyebrow">${escapeHtml(label)}</p><h2>${escapeHtml(version.title)}</h2><p><strong>${escapeHtml(version.summary)}</strong></p><div class="badge-row"><span class="badge trust">${escapeHtml(confidenceLabels[version.confidence] ?? version.confidence)}</span><span class="badge">${version.sources.length} sources</span></div><div class="version-body">${escapeHtml(version.body)}</div>${version.conclusion ? `<section class="version-footer"><h3>Conclusion</h3><p>${escapeHtml(version.conclusion.outcome)}</p><p><strong>Why it mattered:</strong> ${escapeHtml(version.conclusion.historicalSignificance)}</p></section>` : ""}<div class="version-footer"><h3>Truth &amp; Imagination</h3>${renderTruth(version)}<h3>Sources</h3>${renderSources(version)}<button class="primary-button" data-approve-version="${escapeHtml(version.id)}" data-draft-id="${escapeHtml(draft.id)}" ${state.busy ? "disabled" : ""}>Approve ${escapeHtml(label)}</button></div></article>`;
}

function renderApprovedDraft(draft: StoryDraft): string {
  const version = approvedVersion(draft) ?? draft.versionA;
  const assetStatus = draft.status === "complete" ? "Complete" : "Ready for audio and images";
  return `<div class="view-heading"><div><p class="eyebrow">Approved Story</p><h1>${escapeHtml(version.title)}</h1><p class="muted">${escapeHtml(assetStatus)}</p></div><button class="quiet-button" data-view="library">Back to Library</button></div><div class="reader-toolbar"><span class="badge trust">Text approved</span><div class="button-row"><button class="secondary-button" data-read-version="approved">Read to Me</button>${draft.status !== "complete" ? `<button class="primary-button" data-generate-assets="${escapeHtml(draft.id)}" ${state.busy ? "disabled" : ""}>${state.busy ? "Preparing..." : "Create Approved Audio & Images"}</button>` : ""}</div></div><article class="story-sheet"><h1>${escapeHtml(version.title)}</h1><p><strong>${escapeHtml(version.summary)}</strong></p><div id="approved-story-body" class="story-body">${escapeHtml(version.body)}</div>${version.conclusion ? `<h2>Conclusion</h2><p>${escapeHtml(version.conclusion.outcome)}</p><p><strong>Historical significance:</strong> ${escapeHtml(version.conclusion.historicalSignificance)}</p>` : ""}<h2>Family Discussion</h2><ol class="discussion-list">${version.discussionQuestions.map((question) => `<li>${escapeHtml(question)}</li>`).join("")}</ol><h2>Truth &amp; Imagination</h2>${renderTruth(version)}<h2>Sources</h2>${renderSources(version)}</article>`;
}

function renderTruth(version: StoryVersion): string {
  const groups = [
    ["What we know", version.truthAndImagination.whatWeKnow],
    ["What tradition says", version.truthAndImagination.whatTraditionSays],
    ["What we imagined", version.truthAndImagination.whatWeImagined],
    ["What remains uncertain", version.truthAndImagination.whatIsUncertain]
  ] as const;
  return groups.filter(([, values]) => values.length).map(([title, values]) => `<h4>${escapeHtml(title)}</h4><ul class="truth-list">${values.map((value) => `<li>${escapeHtml(value)}</li>`).join("")}</ul>`).join("") || `<p>No imaginative additions were identified.</p>`;
}

function renderSources(version: StoryVersion): string {
  if (!version.sources.length) return `<p>No sources were attached.</p>`;
  return `<ul class="source-list">${version.sources.map((source) => { const url = safeExternalUrl(source.url); return `<li>${url ? `<a href="${escapeHtml(url)}" target="_blank" rel="noreferrer">${escapeHtml(source.title)}</a>` : escapeHtml(source.title)} · ${escapeHtml(source.publisher)} <span class="muted">(${escapeHtml(titleCase(source.type))})</span></li>`; }).join("")}</ul>`;
}

function renderReader(): string {
  const detail = state.activePublicStory;
  if (!detail) return `<section class="empty-state"><h1>Story unavailable</h1><button class="secondary-button" data-view="library">Return to Library</button></section>`;
  const { version, item, assets } = detail;
  const audio = assets.find((asset) => asset.assetType === "audio");
  const images = assets.filter((asset) => asset.assetType === "image");
  return `<div class="reader-toolbar"><button class="quiet-button" data-view="library">Back to Library</button><div class="button-row">${audio ? `<audio id="story-audio" controls preload="metadata" src="/api/catalog/assets/${escapeHtml(audio.id)}"></audio>` : `<button class="secondary-button" data-read-public="true">Device Read Aloud</button>`}</div></div><article class="story-sheet"><p class="eyebrow">Curator's Pick · ${escapeHtml(levelLabels[item.story.level])}</p><h1>${escapeHtml(version.title)}</h1><p><strong>${escapeHtml(version.summary)}</strong></p>${images.length ? `<div class="story-grid">${images.map((image) => `<img src="/api/catalog/assets/${escapeHtml(image.id)}" alt="Historical illustration for ${escapeHtml(version.title)}">`).join("")}</div>` : ""}<div id="public-story-body" class="story-body">${escapeHtml(version.body)}</div>${version.conclusion ? `<h2>Conclusion</h2><p>${escapeHtml(version.conclusion.outcome)}</p><p><strong>Historical significance:</strong> ${escapeHtml(version.conclusion.historicalSignificance)}</p>` : ""}<h2>Family Discussion</h2><ol class="discussion-list">${version.discussionQuestions.map((question) => `<li>${escapeHtml(question)}</li>`).join("")}</ol><h2>Truth &amp; Imagination</h2>${renderTruth(version)}<h2>Sources</h2>${renderSources(version)}</article>`;
}

function renderSearch(): string {
  const query = state.searchQuery.trim().toLowerCase();
  const profile = selectedProfile();
  const level = state.searchLevel;
  const privateResults = state.library.filter((draft) => matchDraft(draft, query, level));
  const publicResults = state.publicCatalogue.filter((item) => {
    if (level !== "all" && item.story.level !== level) return false;
    return !query || [item.story.title, item.story.summary, item.story.topic, item.story.collection, ...(item.story.collections ?? []), ...(item.story.virtues ?? [])].some((value) => String(value ?? "").toLowerCase().includes(query));
  });
  const figureResults = (state.figures?.figures ?? []).filter((figure) => !query || [figure.name, figure.displayTitle, figure.era, ...figure.roles, ...figure.topics, ...figure.locations].some((value) => value.toLowerCase().includes(query)));
  const count = privateResults.length + publicResults.length + figureResults.length;
  return `<div class="view-heading"><div><p class="eyebrow">People · Places · Events · Virtues</p><h1>Search History</h1><p class="muted">Search story text and metadata, including virtues that are shown through the story.</p></div></div><section class="search-surface"><form id="search-form" class="search-controls"><label class="hidden" for="search-query">Search</label><input id="search-query" class="search-box" name="query" value="${escapeHtml(state.searchQuery)}" placeholder="Search stories, people, places, events, or virtues"><label class="hidden" for="search-level">Story level</label><select id="search-level" class="profile-select" name="level"><option value="all">All levels</option>${Object.entries(levelLabels).map(([value, label]) => `<option value="${value}" ${value === level ? "selected" : ""}>${escapeHtml(label)}</option>`).join("")}</select></form><p class="search-summary">${count} ${count === 1 ? "result" : "results"}${profile ? ` for ${escapeHtml(profile.displayName)}` : ""}</p><div class="result-grid">${privateResults.map(renderPrivateStoryCard).join("")}${publicResults.map(renderPublicStoryCard).join("")}${figureResults.map(renderFigureCard).join("")}</div>${count === 0 ? `<div class="empty-state"><h2>No exact match yet.</h2><p class="muted">Try a person, place, time period, event, collection, or virtue.</p></div>` : ""}</section>`;
}

function matchDraft(draft: StoryDraft, query: string, level: StoryLevel | "all"): boolean {
  if (level !== "all" && draft.request.level !== level) return false;
  const version = approvedVersion(draft) ?? draft.versionA;
  const values = [version.title, version.summary, version.body, draft.request.topic, draft.request.continent, draft.request.countryOrPeople, draft.request.timePeriod, draft.request.virtue, ...version.sources.flatMap((source) => [source.title, source.publisher])];
  return !query || values.some((value) => String(value ?? "").toLowerCase().includes(query));
}

function renderFigures(): string {
  const profile = selectedProfile();
  if (!profile) return renderSignInNotice("Sign in to view figures approved for a family's chosen story level.");
  const feature = state.figures?.featured;
  const figures = state.figures?.figures ?? [];
  return `<div class="view-heading"><div><p class="eyebrow">Private, Sourced Conversations</p><h1>Historical Figures</h1><p class="muted">Ask about each figure's own life, people, places, writings, and events.</p></div><button class="secondary-button" data-action="request-figure">Request a Figure</button></div>${feature ? `<section class="feature-band"><img class="feature-image" src="${escapeHtml(apiAssetUrl(feature.figure.portrait?.url) ?? "/images/collection-hidden-heroes.webp")}" alt="${escapeHtml(feature.figure.portrait?.altText ?? "")}"><div class="feature-copy"><p class="eyebrow">Figure of the Day</p><h2>${escapeHtml(feature.figure.name)}</h2><p class="feature-subtitle">${escapeHtml(feature.figure.pageSubtitle)}</p><p class="feature-description">${escapeHtml(feature.figure.pageTitle)} · ${escapeHtml(feature.figure.lifespan)}</p><button class="primary-button" data-open-figure="${escapeHtml(feature.figure.id)}">Explore ${escapeHtml(feature.figure.name)}</button><div class="focus-grid">${feature.focuses.slice(0, 4).map((focus) => `<button class="focus-button" data-open-figure="${escapeHtml(feature.figure.id)}" title="${escapeHtml(focus.description)}"><strong>${escapeHtml(focus.title)}</strong><br><span class="muted">${escapeHtml(focus.description)}</span></button>`).join("")}</div></div></section>` : ""}<section class="section-band"><div class="section-header"><h2>Available Figures</h2><span class="muted">${figures.length} approved</span></div><div class="figure-grid">${figures.map(renderFigureCard).join("")}</div>${figures.length === 0 ? `<div class="empty-state"><h2>Figure packs are being reviewed.</h2><p class="muted">A figure appears only after source, artwork, voice, age, and sensitivity approval.</p></div>` : ""}</section>`;
}

function renderFigureCard(figure: HistoricalFigureSummary): string {
  const image = apiAssetUrl(figure.portrait?.url) ?? "/images/collection-hidden-heroes.webp";
  return `<button class="figure-card" data-open-figure="${escapeHtml(figure.id)}"><img src="${escapeHtml(image)}" alt="${escapeHtml(figure.portrait?.altText ?? "")}"><div class="card-copy"><p class="eyebrow">${escapeHtml(presentationLabel(figure.presentationClass))}</p><h3>${escapeHtml(figure.name)}</h3><p>${escapeHtml(figure.displayTitle)} · ${escapeHtml(figure.lifespan)}</p><div class="metadata-row"><span class="badge">${escapeHtml(figure.era)}</span>${figure.conversationModes.includes("voice") ? `<span class="badge trust">Voice</span>` : `<span class="badge">Text</span>`}</div></div></button>`;
}

function renderFigureDetail(): string {
  const figure = state.activeFigure;
  const profile = selectedProfile();
  if (!figure || !profile) return `<section class="empty-state"><h1>Figure unavailable</h1><button class="secondary-button" data-view="figures">Return to Figures</button></section>`;
  const access = figure.levelAccess.find((item) => item.level === profile.storyLevel)?.interactions ?? [];
  const canText = access.includes("guided") || access.includes("typed");
  const canVoice = state.config?.webVoice?.enabled === true && access.includes("voice") && figure.conversationModes.includes("voice");
  const portrait = apiAssetUrl(figure.portrait?.url) ?? "/images/collection-hidden-heroes.webp";
  return `<div class="view-heading"><button class="quiet-button" data-view="figures">Back to Figures</button></div><section class="figure-hero"><img class="figure-portrait" src="${escapeHtml(portrait)}" alt="${escapeHtml(figure.portrait?.altText ?? "")}"><div class="figure-hero-copy"><p class="eyebrow">${escapeHtml(presentationLabel(figure.presentationClass))}</p><h1 class="figure-title">${escapeHtml(figure.name)}</h1><p class="feature-subtitle">${escapeHtml(figure.pageSubtitle)}</p><p>${escapeHtml(figure.lifespan)} · ${escapeHtml(figure.locations.join(", "))}</p><p class="feature-description">${escapeHtml(figure.biography)}</p><div class="button-row">${canText ? `<button class="primary-button" data-start-conversation="text">${escapeHtml(figure.presentationClass === "sacred_study" ? "Begin Sacred Figure Study" : figure.presentationClass === "history_warning" ? "Open History Guide" : `Text with ${figure.name}`)}</button>` : ""}${canVoice ? `<button class="secondary-button" data-start-conversation="voice">Speak & Listen</button>` : ""}</div></div></section><div class="figure-layout"><section class="form-surface"><p class="eyebrow">What You Can Explore</p><h2>${escapeHtml(figure.pageTitle)}</h2><p>${escapeHtml(figure.knowledgeBoundary)}</p><div class="badge-row">${figure.topics.map((topic) => `<span class="badge">${escapeHtml(topic)}</span>`).join("")}</div><h2>Suggested Questions</h2><div class="suggested-questions">${figure.suggestedQuestions.map((question) => `<button data-prepare-question="${escapeHtml(question)}">${escapeHtml(question)}</button>`).join("")}</div><div class="notice"><strong>AI disclosure</strong><br>${escapeHtml(figure.disclosure)}</div></section><aside class="figure-aside"><section class="scope-panel"><h2>Conversation Boundary</h2><ul><li>The figure's documented life</li><li>People they knew or encountered</li><li>Places and events they experienced</li><li>Approved writings and beliefs</li></ul></section><section class="source-panel"><h2>Approved Sources</h2><ul>${figure.sources.slice(0, 8).map((source) => { const url = safeExternalUrl(source.url); return `<li>${url ? `<a href="${escapeHtml(url)}" target="_blank" rel="noreferrer">${escapeHtml(source.title)}</a>` : escapeHtml(source.title)}</li>`; }).join("")}</ul></section></aside></div>`;
}

function renderConversation(): string {
  const detail = state.conversation;
  const figure = state.activeFigure;
  const profile = selectedProfile();
  if (!detail || !figure || !profile) return `<section class="empty-state"><h1>Conversation unavailable</h1><button class="secondary-button" data-view="figures">Return to Figures</button></section>`;
  const portrait = apiAssetUrl(figure.portrait?.url) ?? "/images/collection-hidden-heroes.webp";
  const interactions = figure.levelAccess.find((item) => item.level === profile.storyLevel)?.interactions ?? [];
  const canType = interactions.includes("typed");
  const canGuide = interactions.includes("guided");
  const isVoice = detail.conversation.mode === "voice";
  return `<div class="view-heading"><button class="quiet-button" data-open-figure="${escapeHtml(figure.id)}">About ${escapeHtml(figure.name)}</button><span class="badge trust">${escapeHtml(levelLabels[profile.storyLevel])}</span></div><div class="figure-layout"><section class="conversation-surface"><header class="conversation-header"><img src="${escapeHtml(portrait)}" alt=""><div><h2>${escapeHtml(detail.conversation.title)}</h2><p class="muted">${escapeHtml(figure.responseAttribution)}</p></div></header><div id="message-list" class="message-list">${detail.messages.length ? detail.messages.map((message) => renderMessage(message, portrait)).join("") : `<div class="notice"><strong>Begin with one of the reviewed questions below.</strong><br><span class="muted">Answers stay inside ${escapeHtml(figure.name)}'s approved historical scope.</span></div>`}</div>${canGuide ? `<div class="suggested-questions">${figure.suggestedQuestions.slice(0, 5).map((question) => `<button data-send-guided="${escapeHtml(question)}">${escapeHtml(question)}</button>`).join("")}</div>` : ""}${canType ? `<form id="conversation-form" class="conversation-compose"><label class="hidden" for="conversation-input">Ask ${escapeHtml(figure.name)} a question</label><input id="conversation-input" name="text" maxlength="500" required placeholder="Ask about this figure's life..."><button class="icon-button" type="submit" aria-label="Send question" ${state.busy ? "disabled" : ""}>➤</button></form>` : ""}${isVoice ? `<div class="button-row" style="padding:0 14px 14px"><button class="secondary-button" data-action="listen-for-question" ${state.busy ? "disabled" : ""}>Use Microphone</button><span class="muted">Captions remain visible for every spoken answer.</span></div>` : ""}</section><aside class="figure-aside"><section class="scope-panel"><h2>Scope</h2><p>${escapeHtml(figure.knowledgeBoundary)}</p></section><section class="credit-panel"><h2>Conversation Credits</h2><p class="muted">Redirected or blocked questions do not consume credits.</p><div class="credit-number"><strong>${state.credits?.proInteractiveCredits ?? 0}</strong><span>Pro Interactive Credits</span></div></section><section class="source-panel"><h2>Source Standard</h2><p class="muted">Each answer is retrieved from this figure's approved pack, then checked for age, scope, safety, and confidence.</p></section></aside></div>`;
}

function renderMessage(message: HistoricalConversationMessage, portrait: string): string {
  const user = message.role === "user";
  const audioUrl = apiAssetUrl(message.audioAsset?.url);
  return `<div class="message-row ${user ? "user" : "figure"}">${user ? "" : `<img class="message-avatar" src="${escapeHtml(portrait)}" alt="">`}<div class="message-bubble"><div>${escapeHtml(message.text)}</div>${message.historianNote ? `<div class="historian-note"><strong>Historian's Note:</strong> ${escapeHtml(message.historianNote)}</div>` : ""}${audioUrl ? `<audio controls preload="metadata" src="${escapeHtml(audioUrl)}"></audio>` : ""}${message.scopeDisposition && message.scopeDisposition !== "answered" ? `<div class="badge-row"><span class="badge warning">${escapeHtml(titleCase(message.scopeDisposition))} · no credit charged</span></div>` : ""}</div></div>`;
}

function renderFamily(): string {
  if (!state.session.authenticated) {
    return `<div class="view-heading"><div><p class="eyebrow">Parent-Controlled</p><h1>Family Account</h1><p class="muted">One parent account keeps family profiles and private history together across iPhone, iPad, and web.</p></div></div><section class="account-panel"><h2>Continue with Apple</h2><p>Your private stories remain private. Apple sign-in connects this browser to the same family account without placing a family access token in browser storage.</p>${state.config?.webAccount.enabled ? `<div class="apple-signin-wrap"><div id="appleid-signin" data-color="black" data-border="true" data-type="sign in" data-mode="center-align" data-width="260" data-height="48"></div></div>` : `<div class="notice warning"><strong>Web sign-in setup is not finished yet.</strong><br><span class="muted">The Pages app is real and ready; the Apple Service ID and return address still need to be approved in the Apple developer account.</span></div>`}<p class="muted">You can browse reviewed public stories without signing in. Creation, private stories, credits, and figure conversations require a parent account.</p></section>`;
  }
  const profile = selectedProfile();
  if (!profile) return renderNewProfileForm();
  const account = state.account;
  return `<div class="view-heading"><div><p class="eyebrow">Parent Controls</p><h1>${escapeHtml(profile.displayName)}</h1><p class="muted">Story level, chapter access, content permissions, and account status.</p></div><button class="secondary-button" data-action="sign-out">Sign Out</button></div>${account?.paused ? renderPausedAccount() : ""}<form id="profile-settings-form" class="account-panel settings-form"><div class="field"><label for="profile-name">Profile name</label><input id="profile-name" name="displayName" maxlength="36" value="${escapeHtml(profile.displayName)}" required></div><div class="field"><label for="profile-level">Story level</label><select id="profile-level" name="storyLevel">${Object.entries(levelLabels).map(([value, label]) => `<option value="${value}" ${value === profile.storyLevel ? "selected" : ""}>${escapeHtml(label)}</option>`).join("")}</select></div><section class="settings-section"><h2>Reading</h2><div class="toggle-list">${renderToggle("opensAllJourneyChapters", "Open every chapter", "Sequential by default; parents can open the whole journey.", profile.opensAllJourneyChapters)}${renderToggle("readsStoriesAloudByDefault", "Read stories aloud by default", "Audio follows the approved text and captions remain available.", profile.readsStoriesAloudByDefault)}</div></section><section class="settings-section"><h2>Content Permissions</h2><div class="toggle-list">${renderToggle("allowsBattleStories", "Battles and tactics", "Always adapted to the selected story level.", profile.allowsBattleStories)}${renderToggle("allowsTragicEndings", "Tragic endings", "Consequences are clear without graphic detail.", profile.allowsTragicEndings)}${renderToggle("allowsInfamousFigures", "Complex and infamous history", "Uses examined conversation or a neutral History Guide.", profile.allowsInfamousFigures)}${renderToggle("allowsSacredHistory", "Sacred history", "Uses respectful source study without impersonating central sacred figures.", profile.allowsSacredHistory !== false)}${renderToggle("allowsHistoricalFigureTyping", "Typed figure questions", "Ages 9-12 need parent permission; ages 6-8 remain guided.", profile.allowsHistoricalFigureTyping === true)}${renderToggle("allowsReligiousConflict", "Religious conflict", "Presents beliefs and consequences respectfully.", profile.allowsReligiousConflict)}${renderToggle("allowsMysteriesAndTheories", "Mysteries and disputed claims", "Clearly labels evidence, uncertainty, and speculation.", profile.allowsMysteriesAndTheories)}</div></section><div class="form-actions button-row"><button class="primary-button" type="submit" ${state.busy ? "disabled" : ""}>Save Family Settings</button></div></form><section class="account-panel account-status-panel"><p class="eyebrow">Account Status</p><h2>${account?.paused ? "Paused" : "Active"}</h2><p>${escapeHtml(account?.message ?? "This family profile is connected to the parent account.")}</p><p class="muted">Apple subscriptions, pause or unpause, purchases, and complete account closure remain in the iPhone or iPad app for launch.</p><div class="button-row"><a class="secondary-button" href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noreferrer">Manage Apple Subscription</a></div></section>${state.credits ? renderCredits() : ""}`;
}

function renderPausedAccount(): string {
  return `<section class="paused-lock"><div class="account-panel"><p class="eyebrow">Account Paused</p><h1>Only Settings Is Available</h1><p>Story creation, the private library, Historical Figures, Across Time, audio, images, and credit spending are locked.</p><p class="muted">Use the iPhone or iPad app to unpause after Apple confirms that the subscription is active and renewing.</p><div class="button-row" style="justify-content:center"><a class="primary-button" href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noreferrer">Manage Apple Subscription</a></div></div></section>`;
}

function renderNewProfileForm(): string {
  return `<div class="view-heading"><div><p class="eyebrow">Parent Setup</p><h1>Create a Family Profile</h1><p class="muted">Choose the level and permissions before opening private features.</p></div><button class="secondary-button" data-action="sign-out">Sign Out</button></div><form id="new-profile-form" class="account-panel settings-form"><div class="field"><label for="new-profile-name">Profile name</label><input id="new-profile-name" name="displayName" maxlength="36" required placeholder="Story Explorer"></div><div class="field"><label for="new-profile-level">Story level</label><select id="new-profile-level" name="storyLevel">${Object.entries(levelLabels).map(([value, label]) => `<option value="${value}">${escapeHtml(label)}</option>`).join("")}</select></div><div class="form-actions"><div class="notice"><strong>Parent permissions can change later.</strong><br><span class="muted">Ages 6-8 use reviewed question buttons for historical figures. Microphones remain unavailable under 13.</span></div><button class="primary-button" type="submit">Create Family Profile</button></div></form>`;
}

function renderToggle(name: string, title: string, detail: string, checked: boolean): string {
  return `<label class="toggle-row"><input type="checkbox" name="${escapeHtml(name)}" ${checked ? "checked" : ""}><span><strong>${escapeHtml(title)}</strong><small>${escapeHtml(detail)}</small></span></label>`;
}

function renderCredits(): string {
  const credits = state.credits!;
  return `<section class="credit-panel" style="margin-top:18px"><p class="eyebrow">Available Credits</p><div class="credit-numbers"><div class="credit-number"><strong>${credits.standardStoryCredits}</strong><span>Standard</span></div><div class="credit-number"><strong>${credits.illustratedStoryCredits}</strong><span>Illustrated</span></div><div class="credit-number"><strong>${credits.advancedLongStoryCredits}</strong><span>Advanced Long</span></div><div class="credit-number"><strong>${credits.proInteractiveCredits}</strong><span>Pro Interactive</span></div></div><p class="muted">Purchases are completed in the iPhone or iPad app and appear here after Apple verification.</p></section>`;
}

function renderSignInNotice(message = "Sign in with the parent Apple account to open private family features on this device."): string {
  return `<section class="empty-state"><p class="eyebrow">Parent Account</p><h2>Keep Your History Together</h2><p class="muted">${escapeHtml(message)}</p><button class="primary-button" data-view="family">Continue to Family Sign In</button></section>`;
}

function approvedVersion(draft: StoryDraft): StoryVersion | undefined {
  if (!draft.approvedVersionId) return undefined;
  return [draft.versionA, draft.versionB].find((version) => version?.id === draft.approvedVersionId);
}

function statusLabel(status: StoryDraft["status"]): string {
  return ({ draft: "Draft", approved: "Approved", asset_generation_pending: "Preparing Audio & Images", complete: "Complete" })[status];
}

function titleCase(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function presentationLabel(value: HistoricalFigureSummary["presentationClass"]): string {
  return ({ young_learner: "Young Learner Approved", complex: "Examined Conversation", history_warning: "History's Warnings", sacred_study: "Sacred Figure Study" })[value];
}

async function configureAppleSignIn(): Promise<void> {
  const account = state.config?.webAccount;
  if (!account?.enabled || !account.clientId || !account.redirectUri || !document.getElementById("appleid-signin")) return;
  const key = `${account.clientId}:${account.redirectUri}`;
  try {
    if (!appleScriptPromise) {
      appleScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("apple_script_failed"));
        document.head.appendChild(script);
      });
    }
    await appleScriptPromise;
    if (!window.AppleID || (appleConfiguredKey === key && appleExpectedState && appleExpectedNonce)) return;
    appleExpectedState = crypto.randomUUID();
    appleExpectedNonce = crypto.randomUUID();
    window.AppleID.auth.init({
      clientId: account.clientId,
      scope: "name email",
      redirectURI: account.redirectUri,
      state: appleExpectedState,
      nonce: appleExpectedNonce,
      usePopup: true
    });
    appleConfiguredKey = key;
  } catch {
    showToast("Apple sign-in could not load. Check the connection and try again.", "error");
  }
}

document.addEventListener("AppleIDSignInOnSuccess", (event) => {
  const detail = (event as CustomEvent<AppleAuthorizationDetail>).detail;
  void completeAppleSignIn(detail);
});

document.addEventListener("AppleIDSignInOnFailure", () => {
  resetAppleSignInAttempt();
  showToast("Apple sign-in was not completed.", "error");
  void configureAppleSignIn();
});

async function completeAppleSignIn(detail: AppleAuthorizationDetail): Promise<void> {
  const authorizationCode = detail.authorization?.code;
  const redirectUri = state.config?.webAccount.redirectUri;
  const nonce = appleExpectedNonce;
  if (!authorizationCode || !redirectUri || !nonce || detail.authorization?.state !== appleExpectedState) {
    resetAppleSignInAttempt();
    showToast("Apple did not return the required sign-in proof.", "error");
    void configureAppleSignIn();
    return;
  }
  const displayName = [detail.user?.name?.firstName, detail.user?.name?.lastName].filter(Boolean).join(" ") || undefined;
  setBusy(true);
  try {
    state.session = await api<WebParentSessionResponse>("/web/auth/apple", {
      method: "POST",
      body: JSON.stringify({
        authorizationCode,
        identityToken: detail.authorization?.id_token,
        redirectUri,
        nonce,
        email: detail.user?.email,
        displayName
      })
    });
    state.selectedProfileId = state.session.profiles[0]?.id;
    state.view = "family";
    resetAppleSignInAttempt();
    showToast("Your family account is connected.");
    await loadProfileData();
  } catch (error) {
    resetAppleSignInAttempt();
    handleError(error);
    void configureAppleSignIn();
  } finally {
    setBusy(false);
  }
}

function resetAppleSignInAttempt(): void {
  appleConfiguredKey = undefined;
  appleExpectedState = undefined;
  appleExpectedNonce = undefined;
}

async function openDraft(id: string): Promise<void> {
  const draft = state.library.find((item) => item.id === id);
  if (!draft) return;
  state.activeDraft = draft;
  state.activeAssets = undefined;
  state.view = "draft";
  render();
  if (draft.approvedVersionId) {
    try {
      state.activeAssets = await api<StoryAssetsResponse>(`/stories/${encodeURIComponent(id)}/assets`);
      state.activeDraft = state.activeAssets.draft;
      render();
    } catch (error) {
      handleError(error);
    }
  }
}

async function openPublicStory(id: string): Promise<void> {
  const profile = selectedProfile();
  state.busy = true;
  render();
  try {
    state.activePublicStory = await api<PublicCuratorStoryDetailResponse>(`/catalog/curators-pick/${encodeURIComponent(id)}${profile ? `?level=${encodeURIComponent(profile.storyLevel)}` : ""}`);
    state.view = "reader";
  } catch (error) {
    handleError(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function openFigure(id: string): Promise<void> {
  const profile = selectedProfile();
  if (!profile) {
    navigate("family");
    return;
  }
  state.busy = true;
  render();
  try {
    state.activeFigure = await api<HistoricalFigureProfile>(`/figures/${encodeURIComponent(id)}?profileId=${encodeURIComponent(profile.id)}`);
    state.conversation = undefined;
    state.voiceSession = undefined;
    state.view = "figure";
  } catch (error) {
    handleError(error);
  } finally {
    state.busy = false;
    render();
  }
}

async function startConversation(mode: "text" | "voice"): Promise<void> {
  const profile = selectedProfile();
  const figure = state.activeFigure;
  if (!profile || !figure) return;
  setBusy(true);
  try {
    const conversation = await api<HistoricalConversationDetailResponse["conversation"]>("/figure-conversations", {
      method: "POST",
      body: JSON.stringify({
        profileId: profile.id,
        figureId: figure.id,
        mode,
        level: profile.storyLevel,
        contentSettings: contentSettings(profile)
      })
    });
    state.conversation = { conversation, messages: [] };
    if (mode === "voice") {
      state.voiceSession = await api<HistoricalVoiceSession>(`/figure-conversations/${encodeURIComponent(conversation.id)}/voice-sessions`, { method: "POST" });
    }
    state.view = "conversation";
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

async function sendConversationMessage(text: string, inputKind: "guided" | "typed" | "transcript"): Promise<void> {
  const detail = state.conversation;
  const profile = selectedProfile();
  if (!detail || !profile || !text.trim()) return;
  setBusy(true);
  try {
    const turn = await api<HistoricalConversationTurnResponse>(`/figure-conversations/${encodeURIComponent(detail.conversation.id)}/messages`, {
      method: "POST",
      body: JSON.stringify({
        text: text.trim(),
        inputKind,
        clientMessageId: crypto.randomUUID(),
        voiceSessionId: state.voiceSession?.id,
        contentSettings: contentSettings(profile)
      })
    });
    state.conversation = {
      conversation: turn.conversation,
      messages: [...detail.messages, turn.userMessage, turn.figureMessage]
    };
    state.voiceSession = turn.voiceSession ?? state.voiceSession;
    state.credits = turn.creditBalance;
    render();
    requestAnimationFrame(() => {
      document.getElementById("message-list")?.scrollTo({ top: 999999, behavior: "smooth" });
    });
    const audio = apiAssetUrl(turn.figureMessage.audioAsset?.url);
    if (audio) void new Audio(audio).play().catch(() => undefined);
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

function listenForQuestion(): void {
  const Recognition = window.SpeechRecognition ?? window.webkitSpeechRecognition;
  if (!Recognition) {
    showToast("This browser does not provide speech transcription. Type the question or use the iPhone app.", "error");
    return;
  }
  const recognition = new Recognition();
  recognition.lang = "en-US";
  recognition.continuous = false;
  recognition.interimResults = false;
  showToast("Listening for one question...");
  recognition.onresult = (event) => {
    const transcript = event.results[0]?.[0]?.transcript?.trim();
    if (transcript) void sendConversationMessage(transcript, "transcript");
  };
  recognition.onerror = () => showToast("The microphone question was not captured. Please try again.", "error");
  recognition.onend = () => undefined;
  recognition.start();
}

function readAloud(text: string): void {
  if (!("speechSynthesis" in window)) {
    showToast("Read aloud is not available in this browser.", "error");
    return;
  }
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = 0.93;
  window.speechSynthesis.speak(utterance);
  showToast("Read aloud started. Sources and discussion questions are not included.");
}

function profileFromForm(form: HTMLFormElement, id: string, existing?: FamilyProfileRecord): FamilyProfileRecord {
  const data = new FormData(form);
  const isNew = !existing;
  return {
    id,
    displayName: String(data.get("displayName") ?? "").trim(),
    storyLevel: String(data.get("storyLevel") ?? "9-12") as StoryLevel,
    allowsBattleStories: isNew ? true : data.has("allowsBattleStories"),
    allowsTragicEndings: isNew ? true : data.has("allowsTragicEndings"),
    allowsInfamousFigures: isNew ? false : data.has("allowsInfamousFigures"),
    allowsSacredHistory: isNew ? true : data.has("allowsSacredHistory"),
    allowsHistoricalFigureTyping: isNew ? false : data.has("allowsHistoricalFigureTyping"),
    opensAllJourneyChapters: isNew ? false : data.has("opensAllJourneyChapters"),
    readsStoriesAloudByDefault: isNew ? true : data.has("readsStoriesAloudByDefault"),
    allowsReligiousConflict: isNew ? false : data.has("allowsReligiousConflict"),
    allowsMysteriesAndTheories: isNew ? false : data.has("allowsMysteriesAndTheories")
  };
}

app.addEventListener("click", (event) => {
  const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action], [data-view], [data-browse-query], [data-open-draft], [data-open-public], [data-open-figure], [data-retry-draft], [data-approve-version], [data-keep-both], [data-generate-assets], [data-start-conversation], [data-send-guided], [data-prepare-question], [data-open-journey], [data-read-version], [data-read-public]");
  if (!target) return;
  const view = target.dataset.view as ViewName | undefined;
  if (view) {
    navigate(view);
    return;
  }
  if (target.dataset.action === "finish-onboarding") {
    window.localStorage.setItem("lts_web_onboarded", "true");
    render();
    return;
  }
  if (target.dataset.action === "sign-out") {
    void signOut();
    return;
  }
  if (target.dataset.action === "request-figure") {
    void requestFigure();
    return;
  }
  if (target.dataset.action === "listen-for-question") {
    listenForQuestion();
    return;
  }
  if (target.dataset.browseQuery) {
    state.searchQuery = target.dataset.browseQuery;
    state.searchLevel = selectedProfile()?.storyLevel ?? "all";
    navigate("search");
    return;
  }
  if (target.dataset.openDraft) void openDraft(target.dataset.openDraft);
  if (target.dataset.openPublic) void openPublicStory(target.dataset.openPublic);
  if (target.dataset.openFigure) void openFigure(target.dataset.openFigure);
  if (target.dataset.retryDraft) void retryDraft(target.dataset.retryDraft);
  if (target.dataset.approveVersion && target.dataset.draftId) void approveVersion(target.dataset.draftId, target.dataset.approveVersion);
  if (target.dataset.keepBoth) void keepBoth(target.dataset.keepBoth);
  if (target.dataset.generateAssets) void generateAssets(target.dataset.generateAssets);
  if (target.dataset.startConversation) void startConversation(target.dataset.startConversation as "text" | "voice");
  if (target.dataset.sendGuided) void sendConversationMessage(target.dataset.sendGuided, "guided");
  if (target.dataset.prepareQuestion) {
    const input = document.getElementById("conversation-input") as HTMLInputElement | null;
    if (input) input.value = target.dataset.prepareQuestion;
    else void startConversation("text").then(() => {
      const nextInput = document.getElementById("conversation-input") as HTMLInputElement | null;
      if (nextInput) nextInput.value = target.dataset.prepareQuestion ?? "";
    });
  }
  if (target.dataset.readVersion) {
    const draft = state.activeDraft;
    const version = draft ? approvedVersion(draft) ?? draft.versionA : undefined;
    if (version) readAloud(version.body);
  }
  if (target.dataset.readPublic && state.activePublicStory) readAloud(state.activePublicStory.version.body);
  if (target.dataset.openJourney) {
    state.searchQuery = state.journeys?.journeys.find((journey) => journey.id === target.dataset.openJourney)?.title ?? "";
    navigate("search");
  }
});

app.addEventListener("change", (event) => {
  const target = event.target as HTMLSelectElement;
  if (target.id === "profile-select") {
    state.selectedProfileId = target.value;
    state.activeDraft = undefined;
    state.activeFigure = undefined;
    state.conversation = undefined;
    void loadProfileData();
  }
  if (target.id === "story-format") {
    state.createFormat = target.value as "story" | "across_time";
    render();
    requestAnimationFrame(() => document.getElementById("story-topic")?.focus());
  }
});

app.addEventListener("input", (event) => {
  const target = event.target as HTMLInputElement | HTMLSelectElement;
  if (target.id === "search-query") {
    state.searchQuery = target.value;
    renderSearchInPlace();
  }
  if (target.id === "search-level") {
    state.searchLevel = target.value as StoryLevel | "all";
    renderSearchInPlace();
  }
});

function renderSearchInPlace(): void {
  const main = document.getElementById("main-content");
  if (!main || state.view !== "search") return;
  const focused = document.activeElement?.id;
  const selection = document.activeElement instanceof HTMLInputElement ? document.activeElement.selectionStart : null;
  main.innerHTML = renderSearch();
  const next = focused ? document.getElementById(focused) as HTMLInputElement | null : null;
  next?.focus();
  if (next && selection !== null) next.setSelectionRange(selection, selection);
}

app.addEventListener("submit", (event) => {
  event.preventDefault();
  const form = event.target as HTMLFormElement;
  if (form.id === "create-story-form") void createStory(form);
  if (form.id === "search-form") return;
  if (form.id === "conversation-form") {
    const input = form.elements.namedItem("text") as HTMLInputElement;
    const text = input.value;
    input.value = "";
    void sendConversationMessage(text, "typed");
  }
  if (form.id === "profile-settings-form") void saveProfile(form, false);
  if (form.id === "new-profile-form") void saveProfile(form, true);
});

async function createStory(form: HTMLFormElement): Promise<void> {
  const profile = selectedProfile();
  if (!profile) return;
  const data = new FormData(form);
  const format = String(data.get("format")) as "story" | "across_time";
  const request: StoryRequest = {
    profileId: profile.id,
    format,
    level: profile.storyLevel,
    topic: String(data.get("topic") ?? "").trim(),
    continent: String(data.get("continent") ?? "").trim() || undefined,
    countryOrPeople: String(data.get("countryOrPeople") ?? "").trim() || undefined,
    timePeriod: String(data.get("timePeriod") ?? "").trim() || undefined,
    viewpoint: String(data.get("viewpoint")) as StoryRequest["viewpoint"],
    tone: String(data.get("tone")) as StoryRequest["tone"],
    virtue: String(data.get("virtue")) as StoryRequest["virtue"],
    package: String(data.get("package")) as StoryRequest["package"],
    contentSettings: contentSettings(profile)
  };
  if (format === "across_time") {
    request.participants = [String(data.get("participantOne") ?? "").trim(), String(data.get("participantTwo") ?? "").trim()].filter(Boolean).map((name) => ({ name }));
    request.question = String(data.get("question") ?? "").trim();
    request.dialogueMode = "two_voices";
  }
  setBusy(true);
  try {
    state.activeDraft = await api<StoryDraft>("/stories/draft", { method: "POST", body: JSON.stringify(request) });
    state.library = [state.activeDraft, ...state.library.filter((item) => item.id !== state.activeDraft!.id)];
    state.view = "draft";
    showToast("Version A is ready for review.");
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

async function retryDraft(id: string): Promise<void> {
  setBusy(true);
  try {
    state.activeDraft = await api<StoryDraft>(`/stories/${encodeURIComponent(id)}/retry`, { method: "POST" });
    replaceLibraryDraft(state.activeDraft);
    showToast("Version B is ready. Compare both before choosing.");
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

async function approveVersion(id: string, versionId: string): Promise<void> {
  setBusy(true);
  try {
    const result = await api<{ draft: StoryDraft }>(`/stories/${encodeURIComponent(id)}/approve`, { method: "POST", body: JSON.stringify({ versionId }) });
    state.activeDraft = result.draft;
    replaceLibraryDraft(result.draft);
    showToast("Text approved. Audio and images can now be created.");
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

async function keepBoth(id: string): Promise<void> {
  setBusy(true);
  try {
    const result = await api<{ draft: StoryDraft; creditBalance: CreditBalanceResponse }>(`/stories/${encodeURIComponent(id)}/keep-both`, { method: "POST" });
    state.activeDraft = result.draft;
    state.credits = result.creditBalance;
    replaceLibraryDraft(result.draft);
    showToast("Both versions are preserved as private approved stories.");
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

async function generateAssets(id: string): Promise<void> {
  setBusy(true);
  try {
    await api(`/stories/${encodeURIComponent(id)}/assets/generate`, { method: "POST" });
    state.activeAssets = await api<StoryAssetsResponse>(`/stories/${encodeURIComponent(id)}/assets`);
    state.activeDraft = state.activeAssets.draft;
    replaceLibraryDraft(state.activeAssets.draft);
    showToast(state.activeAssets.nextStep);
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

function replaceLibraryDraft(draft: StoryDraft): void {
  state.library = state.library.map((item) => item.id === draft.id ? draft : item);
}

async function saveProfile(form: HTMLFormElement, isNew: boolean): Promise<void> {
  const existing = isNew ? undefined : selectedProfile();
  const profile = profileFromForm(form, existing?.id ?? `profile_${crypto.randomUUID()}`, existing);
  setBusy(true);
  try {
    const result = await api<FamilyProfileSyncResponse>(`/profiles/${encodeURIComponent(profile.id)}/settings`, { method: "POST", body: JSON.stringify(profile) });
    const profiles = state.session.profiles.filter((item) => item.id !== result.profile.id);
    state.session = { ...state.session, profiles: [...profiles, result.profile] };
    state.selectedProfileId = result.profile.id;
    showToast(isNew ? "Family profile created." : "Family settings saved.");
    await loadProfileData();
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

async function signOut(): Promise<void> {
  setBusy(true);
  try {
    await api<{ signedOut: boolean }>("/web/sign-out", { method: "POST" });
    state.session = { authenticated: false, profiles: [] };
    state.selectedProfileId = undefined;
    state.account = undefined;
    state.library = [];
    state.credits = undefined;
    state.journeys = undefined;
    state.figures = undefined;
    state.view = "home";
    appleConfiguredKey = undefined;
    showToast("Signed out of this browser.");
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

async function requestFigure(): Promise<void> {
  const profile = selectedProfile();
  if (!profile) return;
  const requestedName = window.prompt("Which historical figure would your family like us to review?")?.trim();
  if (!requestedName) return;
  const reason = window.prompt("What would you like to learn about this person? (Optional)")?.trim();
  setBusy(true);
  try {
    await api("/figure-requests", { method: "POST", body: JSON.stringify({ profileId: profile.id, requestedName, reason: reason || undefined }) });
    showToast("Your request was sent for source, safety, and artwork review.");
  } catch (error) {
    handleError(error);
  } finally {
    setBusy(false);
  }
}

void boot();
